import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  type CreateMeetingParams,
  type ScheduledMeeting,
  useScheduledMeetings,
  useScheduledMeetingsMutations,
} from '../../../contexts';
import { useUser } from '../../../hooks';
import { useCalendarTypes } from '../../../hooks/useCalendarTypes';
import { calendarService } from '../../../services/calendarService';
import type { CalendarEvent } from '../../../types/api';
import { SCHEDULE_MEETING_DRAFT_QUERY_KEY } from '../helpers/constants';
import { getNext30MinSlotStartDate, getNextAvailableStartDate } from '../helpers/form';
import type { UseScheduleMeetingOnOpenProps } from '../types';

const useScheduleMeetingOnOpen = ({
  isOpen,
  setMeeting,
  toastHelpers,
  skipCreation = false,
  isProfileReady = true,
  defaultMeetingId,
}: UseScheduleMeetingOnOpenProps) => {
  const { create } = useScheduledMeetingsMutations();
  const { getMeeting } = useScheduledMeetings();
  const { isTokenFresh } = useUser();
  const {
    data: accountConnections,
    refetch: refetchCalendarTypes,
    isFetching: isRefreshingTokens,
  } = useCalendarTypes();

  type OnOpenData = {
    meeting?: ScheduledMeeting;
    suggestedStartDate: Date;
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [...SCHEDULE_MEETING_DRAFT_QUERY_KEY, accountConnections, defaultMeetingId],
    queryFn: async () => {
      calendarService.refreshAuthToken();
      let freshConnections = accountConnections;
      if (!isTokenFresh) {
        const refreshedConnectionsResult = await refetchCalendarTypes();
        freshConnections = refreshedConnectionsResult.data ?? accountConnections;
      }

      const allUpcomingEvents: CalendarEvent[] = [];

      if (freshConnections?.google?.connected) {
        const page = await calendarService.fetchGoogleCalendarEventsList(
          freshConnections.google,
          'upcoming',
          undefined,
          false,
          20,
        );
        const events = (page.events ?? []) as CalendarEvent[];
        allUpcomingEvents.push(...events);
      }

      if (freshConnections?.microsoft?.connected) {
        const page = await calendarService.fetchMicrosoftCalendarEventsList(
          freshConnections.microsoft,
          'upcoming',
          undefined,
          false,
          20,
        );
        const events = (page.events ?? []) as CalendarEvent[];
        allUpcomingEvents.push(...events);
      }

      const startDate =
        allUpcomingEvents.length > 0
          ? getNextAvailableStartDate(allUpcomingEvents, new Date())
          : getNext30MinSlotStartDate(new Date());

      let meeting: ScheduledMeeting | undefined;
      if (!skipCreation) {
        const meetingRequest: CreateMeetingParams = {
          description: null,
          password: '',
        };
        meeting = await create(meetingRequest).catch((error) => {
          toastHelpers.showError('Failed to allocate meeting', (error as Error).message);
          throw error;
        });
      } else if (defaultMeetingId) {
        meeting = (await getMeeting(defaultMeetingId)) ?? undefined;
      }

      return {
        meeting,
        suggestedStartDate: startDate,
      } as OnOpenData;
    },
    enabled: isOpen && !!accountConnections && isProfileReady && !isRefreshingTokens,
    staleTime: 0,
    retry: false,
    // Refetching allocates a second room and resets the meeting-ID dropdown back
    // to whatever this query picked, discarding the user's selection.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.meeting) {
      setMeeting(data.meeting);
    }
  }, [data, setMeeting]);

  return {
    isLoading,
    isError,
    error,
    suggestedStartDate: data?.suggestedStartDate,
  };
};

export default useScheduleMeetingOnOpen;
